import React, { useEffect, useMemo, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { AnimatePresence, motion } from "framer-motion";
import type { WaitingRoomRacer } from "../../api/types";

interface RaceWaitingIndicatorProps {
  participantCount?: number;
  /** Legacy strip (emojis only), used when the backend sends no `racers`. */
  participantEmojis?: string[];
  /** Racer strip with the no-emoji penalty each racer carries (aotdrace-2.1). */
  racers?: WaitingRoomRacer[];
  // when generation is no longer active, the parent should unmount this
  // component or pass active={false}; this lets the internal timer reset.
  active: boolean;
  // optional override of the reveal threshold (ms)
  revealAfterMs?: number;
}

const DEFAULT_REVEAL_MS = 5000;
const MAX_VISIBLE_RACERS = 12;

const pulse = keyframes`
  0%   { transform: scale(1);    opacity: 0.85; }
  50%  { transform: scale(1.08); opacity: 1; }
  100% { transform: scale(1);    opacity: 0.85; }
`;

const Wrap = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  width: 100%;
`;

const RacerRow = styled.div`
  display: flex;
  gap: 10px 8px;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 340px;
`;

const Racer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  min-width: 34px;
`;

const Dot = styled.div<{ $delay: number; $penalized: boolean }>`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: ${(p) => (p.$penalized ? "rgba(245, 158, 11, 0.16)" : "rgba(120, 120, 120, 0.12)")};
  box-shadow: ${(p) => (p.$penalized ? "inset 0 0 0 1.5px rgba(217, 119, 6, 0.55)" : "none")};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  line-height: 1;
  animation: ${pulse} 1.4s ease-in-out infinite;
  animation-delay: ${(p) => p.$delay}ms;
  user-select: none;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const PenaltyTag = styled(motion.span)`
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: #b45309;
  background: #fef3c7;
  border-radius: 999px;
  padding: 1px 5px;
  line-height: 1.3;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

const Overflow = styled(motion.div)`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: rgba(120, 120, 120, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: #555;
  user-select: none;
`;

const Caption = styled(motion.div)`
  font-size: 0.85rem;
  color: #555;
  text-align: center;
  font-weight: 500;
`;

const Legend = styled(motion.div)`
  font-size: 0.72rem;
  color: #92400e;
  text-align: center;
  max-width: 320px;
`;

const AloneHint = styled(motion.div)`
  font-size: 0.85rem;
  color: #888;
  text-align: center;
  font-style: italic;
`;

const fade = {
  initial: { opacity: 0, y: 4 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
  transition: { duration: 0.25, ease: "easeOut" },
} as const;

/** Stable keys for the strip: emoji plus its occurrence (fallback emojis repeat). */
function keyed(racers: WaitingRoomRacer[]): Array<WaitingRoomRacer & { key: string }> {
  const seen = new Map<string, number>();
  return racers.map((r) => {
    const n = seen.get(r.emoji) ?? 0;
    seen.set(r.emoji, n + 1);
    return { ...r, key: `${r.emoji}#${n}` };
  });
}

/**
 * The anonymous "N users think they got the aotd" strip on the waiting screen.
 * Racers pop in as they join and a racer carrying the no-emoji penalty wears a
 * "+450ms" tag. Revealed only after a few seconds of active generation, and it
 * never says who won: the finished art is the reveal.
 */
const RaceWaitingIndicator: React.FC<RaceWaitingIndicatorProps> = ({
  participantCount,
  participantEmojis,
  racers,
  active,
  revealAfterMs = DEFAULT_REVEAL_MS,
}) => {
  const [revealed, setRevealed] = useState<boolean>(false);
  const startedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!active) {
      setRevealed(false);
      startedAtRef.current = null;
      return;
    }
    if (startedAtRef.current == null) {
      startedAtRef.current = Date.now();
    }
    const elapsed = Date.now() - startedAtRef.current;
    if (elapsed >= revealAfterMs) {
      setRevealed(true);
      return;
    }
    const t = setTimeout(() => setRevealed(true), revealAfterMs - elapsed);
    return () => clearTimeout(t);
  }, [active, revealAfterMs]);

  const strip = useMemo(() => {
    const base: WaitingRoomRacer[] =
      Array.isArray(racers) && racers.length > 0
        ? racers
        : (participantEmojis ?? []).map((emoji) => ({ emoji, penaltyMs: 0 }));
    return keyed(base.slice(0, MAX_VISIBLE_RACERS));
  }, [racers, participantEmojis]);

  // wait for a real count before deciding; avoids flashing "you're alone" while
  // the first /status response is still in flight.
  const count = Math.max(
    typeof participantCount === "number" ? participantCount : 0,
    strip.length,
  );
  const show = active && revealed && count > 0;
  const penalized = strip.filter((r) => r.penaltyMs > 0);
  const overflow = Math.max(0, count - strip.length);

  return (
    <AnimatePresence initial={false} mode="wait">
      {!show ? null : count === 1 ? (
        <Wrap key="alone" {...fade}>
          <AloneHint>looks like you're alone in this one...</AloneHint>
          {penalized.length > 0 ? (
            <Legend>
              their time carries +{penalized[0].penaltyMs}ms for racing with no emojis staged
            </Legend>
          ) : null}
        </Wrap>
      ) : (
        <Wrap key="strip" {...fade} layout>
          <RacerRow>
            <AnimatePresence initial={false}>
              {strip.map((r, i) => (
                <Racer
                  key={r.key}
                  layout
                  initial={{ opacity: 0, scale: 0.4 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.4 }}
                  transition={{ type: "spring", stiffness: 420, damping: 26 }}
                  aria-label={
                    r.penaltyMs > 0 ? `${r.emoji}, +${r.penaltyMs}ms penalty` : r.emoji
                  }
                >
                  <Dot $delay={(i % 6) * 120} $penalized={r.penaltyMs > 0}>
                    {r.emoji}
                  </Dot>
                  <AnimatePresence initial={false}>
                    {r.penaltyMs > 0 ? (
                      <PenaltyTag key="penalty" {...fade}>
                        +{r.penaltyMs}ms
                      </PenaltyTag>
                    ) : null}
                  </AnimatePresence>
                </Racer>
              ))}
              {overflow > 0 ? (
                <Overflow
                  key="overflow"
                  layout
                  initial={{ opacity: 0, scale: 0.4 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.4 }}
                >
                  +{overflow}
                </Overflow>
              ) : null}
            </AnimatePresence>
          </RacerRow>
          <Caption layout>
            <AnimatePresence initial={false} mode="popLayout">
              <motion.span key={count} {...fade} style={{ display: "inline-block" }}>
                {count}
              </motion.span>
            </AnimatePresence>{" "}
            users think they got the aotd
          </Caption>
          <AnimatePresence initial={false}>
            {penalized.length > 0 ? (
              <Legend key="legend" layout {...fade}>
                +{penalized[0].penaltyMs}ms: raced with no emojis staged for their next AOTD
              </Legend>
            ) : null}
          </AnimatePresence>
        </Wrap>
      )}
    </AnimatePresence>
  );
};

export default RaceWaitingIndicator;
